import React, { useState, forwardRef } from "react";
import { useForm } from "react-hook-form";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import { FormControl, FormHelperText, InputLabel } from "@mui/material";
import useFetch from "../../../../../hooks/useFetch";
import ModalShell from "../../../../shares/dashboard/ModalShell";

const FORM_ID = "add-group-form";

const DAYS = [
  "شنبه",
  "یکشنبه",
  "دوشنبه",
  "سه شنبه",
  "چهارشنبه",
  "پنج شنبه",
  "جمعه",
];
const GRADES = ["دهم", "یازدهم", "دوازدهم"];

/**
 * Create / edit a class group. forwardRef because it is the direct child of an
 * MUI <Modal>, which passes a ref down. The fetch wiring (POST vs PATCH keyed on
 * `edit`, GET to prefill) is unchanged - only the shell and field layout moved
 * onto the shared design system.
 */
const AddGroups = forwardRef(({ close, get, edit }, ref) => {
  const [call, setCall] = useState({
    name: "",
    group_time: "",
    group_day: "",
    group_gender: "",
    group_grade: "",
  });

  const newGroup = useFetch({
    method: "POST",
    url: `management/groups/`,
    noHeader: false,
    trigger: false,
    data: call,
    caller: get,
  });
  const editGroup = useFetch({
    method: "PATCH",
    url: `management/groups/${edit}/`,
    noHeader: false,
    trigger: false,
    data: call,
    caller: get,
  });
  useFetch({
    method: "GET",
    url: `management/groups/${edit}/`,
    noHeader: false,
    trigger: edit ? true : false,
    setter: setCall,
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({ values: call });

  const onSubmit = () => {
    const formData = new FormData();
    formData.append("name", call.name);
    formData.append("group_time", call.group_time);
    formData.append("group_day", call.group_day);
    formData.append("group_gender", call.group_gender);
    formData.append("group_grade", call.group_grade);
    setCall(formData);
    if (edit) {
      editGroup.reFetch();
    } else {
      newGroup.reFetch();
    }
  };

  React.useEffect(() => {
    newGroup.status === 201 && close();
  }, [newGroup.status]);
  React.useEffect(() => {
    editGroup.status === 200 && close();
  }, [editGroup.status]);

  const submitting = newGroup.loading || editGroup.loading;

  return (
    <ModalShell
      ref={ref}
      close={close}
      title={edit ? "ویرایش گروه" : "گروه جدید"}
      subtitle="مشخصات گروه کلاسی را وارد کنید"
      footer={
        <div className="flex justify-start">
          <button
            form={FORM_ID}
            type="submit"
            disabled={submitting}
            className="rounded-soft bg-primary px-2xl py-sm text-base text-white transition-colors duration-150 hover:bg-primary-hover active:bg-primary-active focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-60 motion-reduce:transition-none"
          >
            {submitting ? "در حال ثبت…" : "ثبت گروه"}
          </button>
        </div>
      }
    >
      <form
        id={FORM_ID}
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col gap-xl"
        noValidate
      >
        <TextField
          label="عنوان"
          value={call.name}
          {...register("name", { required: "عنوان را وارد کنید" })}
          error={!!errors.name}
          helperText={errors.name?.message}
          onChange={(e) => setCall({ ...call, name: e.target.value })}
          fullWidth
        />

        <FormControl error={!!errors.group_day} fullWidth>
          <InputLabel id="group-day-label">روز</InputLabel>
          <Select
            labelId="group-day-label"
            label="روز"
            value={call.group_day}
            {...register("group_day", { required: "روز را انتخاب کنید" })}
            onChange={(e) => setCall({ ...call, group_day: e.target.value })}
          >
            {DAYS.map((day) => (
              <MenuItem key={day} value={day}>
                {day}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>{errors?.group_day?.message}</FormHelperText>
        </FormControl>

        <TextField
          label="ساعت"
          value={call.group_time}
          {...register("group_time", { required: "ساعت را وارد کنید" })}
          error={!!errors.group_time}
          helperText={errors.group_time?.message}
          onChange={(e) => setCall({ ...call, group_time: e.target.value })}
          fullWidth
        />

        <FormControl error={!!errors.group_grade} fullWidth>
          <InputLabel id="group-grade-label">مقطع</InputLabel>
          <Select
            labelId="group-grade-label"
            label="مقطع"
            value={call.group_grade}
            {...register("group_grade", { required: "مقطع را انتخاب کنید" })}
            onChange={(e) => setCall({ ...call, group_grade: e.target.value })}
          >
            {GRADES.map((grade) => (
              <MenuItem key={grade} value={grade}>
                {grade}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>{errors?.group_grade?.message}</FormHelperText>
        </FormControl>
      </form>
    </ModalShell>
  );
});

export default AddGroups;
